import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Play, Download, Phone, Star, CheckCircle2, Mic, Linkedin, Calculator } from "lucide-react";
import { Reveal, SectionTitle } from "../components/common";
import { getIcon } from "../lib/icons";
import { publicApi, mediaUrl, mediaSrcSet } from "../lib/api";
import { videoEmbedUrl } from "../lib/utils";
import GoogleRatingBadge from "../components/GoogleRatingBadge";
import GoogleReviewsCarousel from "../components/GoogleReviewsCarousel";import GuideDownloadModal from "../components/GuideDownloadModal";
import HomePromoPopup from "../components/HomePromoPopup";
import FeaturedStoryTicker from "../components/FeaturedStoryTicker";
import { CaseStudyCard } from "../components/CaseStudyCard";
import { FounderSection } from "../components/FounderSection";
import { AnnouncementBar } from "../components/AnnouncementBar";
import BookCallButton from "../components/BookCallButton";
import Seo from "../components/Seo";

const HERO_FALLBACK = "https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const FloatingNumbers = () => {
  const nums = React.useMemo(
    () => Array.from({ length: 28 }).map(() => ({
      n: Math.floor(Math.random() * 10), left: Math.random() * 100, top: Math.random() * 100,
      size: 20 + Math.random() * 70, opacity: 0.04 + Math.random() * 0.1,
      dur: 6 + Math.random() * 8, delay: Math.random() * 5,
    })), []
  );
  return (
    <div className="absolute inset-0 pointer-events-none select-none" aria-hidden>
      {/* The digit is decoration, so it is drawn by CSS (.float-num::before reads
          --n) rather than being element text. As element text it landed in
          document.body.innerText — 112 loose digits per homepage — which text
          extractors and AI answer engines read as content. */}
      {nums.map((x, i) => (
        <span key={i} className="float-num absolute font-display font-extrabold text-[#7CC5FB]"
          style={{ "--n": `"${x.n}"`, left: `${x.left}%`, top: `${x.top}%`, fontSize: `${x.size}px`, opacity: x.opacity, animation: `floatNum ${x.dur}s ease-in-out ${x.delay}s infinite` }} />
      ))}
    </div>
  );
};

function CountUp({ end, suffix = "", duration = 1600 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  const isDecimal = !Number.isInteger(end);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setVal(isDecimal ? +(end * eased).toFixed(1) : Math.round(end * eased));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [end, duration, isDecimal]);
  const shown = isDecimal
    ? val.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : val.toLocaleString();
  return <span ref={ref}>{shown}{suffix}</span>;
}

export default function Home() {
  const [hp, setHp] = useState(null);
  const [services, setServices] = useState([]);
  const [videos, setVideos] = useState([]);
  const [rating, setRating] = useState(null);
  const [blog, setBlog] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [guides, setGuides] = useState([]);
  const [stories, setStories] = useState([]);
  const [activeGuide, setActiveGuide] = useState(null);

  useEffect(() => {
    publicApi.homepage().then(setHp).catch(() => {});
    publicApi.services().then(setServices).catch(() => {});
    publicApi.videoTestimonials().then(setVideos).catch(() => {});
    publicApi.googleRating().then(setRating).catch(() => {});
    publicApi.blog().then(setBlog).catch(() => {});
    publicApi.podcasts().then(setPodcasts).catch(() => {});
    publicApi.guides().then(setGuides).catch(() => {});
    publicApi.caseStudies().then(setStories).catch(() => {});
  }, []);

  if (!hp) return <div className="min-h-screen numbers-pattern" />;

  const svcBySlug = Object.fromEntries(services.map((s) => [s.slug, s]));
  const heroImg = hp.hero.image ? mediaUrl(hp.hero.image) : HERO_FALLBACK;
  const teamImg = hp.team_image ? mediaUrl(hp.team_image, 1000) : HERO_FALLBACK;
  const latestArticle = blog[0];
  const latestPodcast = podcasts[0];
  const featuredGuide = guides.find((g) => g.id === hp.featured_guide_id) || guides.find((g) => g.file_url) || guides[0];

  return (
    <div>
      <Helmet>
        {hp.hero.image && <link rel="preload" as="image" href={mediaUrl(hp.hero.image, 1080)} fetchPriority="high" />}
      </Helmet>
      <Seo description="Outsourced finance team & Xero Platinum Partner accountants in Tamworth, Sutton Coldfield & Birmingham. Bookkeeping, VAT, payroll, cashflow forecasting and strategic advice to help your business make better decisions and grow — more than numbers." />
      <HomePromoPopup />
      <AnnouncementBar announcement={hp.announcement} />

      {/* ── Section 1 · Hero ───────────────────────────── */}
      <section className="numbers-pattern text-white">
        <FloatingNumbers />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <span className="animate-fade-up inline-block text-orange font-bold text-sm tracking-widest uppercase mb-5">{hp.hero.eyebrow}</span>
              <h1 className="animate-fade-up font-display font-extrabold text-4xl md:text-6xl leading-[0.98] tracking-tight">{hp.hero.headline}</h1>
              <p className="animate-fade-up mt-6 text-lg text-white font-semibold max-w-xl leading-snug" style={{ animationDelay: "0.1s" }}>{hp.hero.subheading}</p>
              <p className="animate-fade-up mt-4 text-base text-slate-300 max-w-xl leading-relaxed" style={{ animationDelay: "0.15s" }}>{hp.hero.intro}</p>
              <div className="animate-fade-up mt-9 flex flex-wrap gap-4" style={{ animationDelay: "0.2s" }}>
                <Link to={hp.hero.primary_cta_link} data-testid="hero-primary-cta" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:shadow-xl hover:shadow-orange/30 hover:-translate-y-0.5">{hp.hero.primary_cta_label} <ArrowRight size={18} /></Link>
                <BookCallButton testId="hero-book-call" label="Book a Call" className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-bold px-8 py-4 rounded-full transition-all bg-transparent" />
                <Link to={hp.hero.secondary_cta_link} data-testid="hero-secondary-cta" className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-bold px-8 py-4 rounded-full transition-all">{hp.hero.secondary_cta_label}</Link>
              </div>
              <div className="animate-fade-up mt-8" style={{ animationDelay: "0.3s" }}><GoogleRatingBadge dark /></div>
              <FeaturedStoryTicker stories={stories} />
            </div>
            <div className="animate-fade-up relative" style={{ animationDelay: "0.15s" }}>
              <div className="absolute -inset-3 bg-orange/20 rounded-[2rem] blur-2xl" aria-hidden />
              <img loading="eager" fetchPriority="high" decoding="async" src={heroImg} srcSet={mediaSrcSet(hp.hero.image)} sizes="(max-width: 767px) 100vw, 520px" alt="The CASS team supporting growing businesses" className="relative rounded-3xl object-cover w-full h-[340px] md:h-[480px] shadow-2xl border border-white/10" data-testid="hero-image" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 2 · Outcomes ───────────────────────── */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><SectionTitle eyebrow="Helping you move forward" title={hp.outcomes_heading} subtitle={hp.outcomes_intro} center /></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {hp.outcomes.map((o, i) => {
              const Icon = getIcon(o.icon);
              return (
                <Reveal key={i} delay={i * 70}>
                  <div className="group h-full bg-slate-50 rounded-2xl p-8 border border-navy/5 hover:border-orange/30 hover:-translate-y-1 transition-all duration-300" data-testid={`outcome-${i}`}>
                    <div className="w-14 h-14 rounded-xl bg-white shadow-sm flex items-center justify-center text-orange group-hover:bg-orange group-hover:text-white transition-colors mb-5"><Icon size={26} /></div>
                    <h3 className="font-display font-bold text-xl text-navy mb-2">{o.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{o.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Section 3 · Why CASS + counters ────────────── */}
      <section className="numbers-pattern text-white relative">
        <FloatingNumbers />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-28">
          <Reveal><SectionTitle eyebrow="Why CASS" title={hp.why_heading} subtitle={hp.why_intro} center light /></Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto mb-16">
            {hp.differentiators.map((d, i) => {
              const Icon = getIcon(d.icon);
              return (
                <Reveal key={i} delay={i * 50}>
                  <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full pl-3 pr-5 py-3 border border-white/10" data-testid={`differentiator-${i}`}>
                    <span className="w-9 h-9 rounded-full bg-orange flex items-center justify-center shrink-0"><Icon size={17} className="text-white" /></span>
                    <span className="text-sm font-semibold">{d.label}</span>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {hp.stats.map((st, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="text-center" data-testid={`stat-counter-${i}`}>
                  <div className="font-display font-extrabold text-4xl md:text-5xl text-orange mb-2"><CountUp end={st.value} suffix={st.suffix} /></div>
                  <div className="text-sm text-slate-300">{st.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Section 4 · Services (3 categories) ────────── */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><SectionTitle eyebrow="Our services" title={hp.services_heading} subtitle={hp.services_intro} center /></Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {hp.service_categories.map((cat, i) => {
              const Icon = getIcon(cat.icon);
              const items = (cat.slugs || []).map((entry) => {
                const [sl, ...rest] = String(entry).split("|");
                const svc = svcBySlug[sl.trim()];
                if (!svc) return null;
                return { slug: svc.slug, title: rest.length ? rest.join("|").trim() : svc.title };
              }).filter(Boolean);
              return (
                <Reveal key={i} delay={i * 100}>
                  <div className="h-full bg-white rounded-2xl p-8 border border-navy/5 shadow-sm flex flex-col" data-testid={`service-category-${i}`}>
                    <div className="w-14 h-14 rounded-xl bg-navy/5 flex items-center justify-center text-navy mb-5"><Icon size={26} /></div>
                    <h3 className="font-display font-bold text-xl text-navy mb-2">{cat.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed mb-6">{cat.intro}</p>
                    <ul className="space-y-2">
                      {items.map((s, idx) => (
                        <li key={`${s.slug}-${idx}`}>
                          <Link to={s.external_path || `/services/${s.slug}`} className="group flex items-center gap-2 text-sm font-semibold text-navy hover:text-orange transition-colors" data-testid={`service-link-${s.slug}-${idx}`}>
                            <CheckCircle2 size={15} className="text-orange shrink-0" />
                            <span className="group-hover:underline">{s.title}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Link to="/services" className="inline-flex items-center gap-2 text-orange font-bold hover:gap-3 transition-all" data-testid="services-view-all">View all services <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* ── Three Pillars of Smart Finance ─────────────── */}
      <section className="numbers-pattern text-white relative" data-testid="pillars-section">
        <FloatingNumbers />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal><SectionTitle eyebrow={hp.pillars_eyebrow} title={hp.pillars_heading} subtitle={hp.pillars_intro} center light /></Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {(hp.pillars || []).map((p, i) => {
              const Icon = getIcon(p.icon);
              return (
                <Reveal key={i} delay={i * 110}>
                  <div className="group h-full bg-white/[0.06] backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-orange/40 hover:-translate-y-1 transition-all duration-300" data-testid={`pillar-${i}`}>
                    <div className="flex items-center justify-between mb-6">
                      <span className="w-14 h-14 rounded-xl bg-orange flex items-center justify-center text-white shrink-0"><Icon size={26} /></span>
                      <span className="font-display font-extrabold text-5xl text-white/10 group-hover:text-orange/30 transition-colors leading-none">{p.number}</span>
                    </div>
                    <h3 className="font-display font-bold text-xl text-white mb-3 leading-snug">{p.title}</h3>
                    <p className="text-sm text-slate-300 leading-relaxed">{p.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Section 5 · Meet the team ──────────────────── */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <div className="relative">
                <div className="absolute -inset-3 bg-navy/5 rounded-[2rem]" aria-hidden />
                <img loading="lazy" decoding="async" src={teamImg} alt="The CASS team" className="relative rounded-3xl object-cover w-full h-[340px] md:h-[440px] shadow-lg" data-testid="team-image" />
              </div>
            </Reveal>
            <Reveal delay={120}>
              <span className="inline-flex items-center gap-2 text-orange font-bold text-sm tracking-widest uppercase mb-3"><Phone size={15} /> A team you can reach</span>
              <h2 className="font-display font-extrabold text-3xl md:text-4xl text-navy leading-tight mb-5">{hp.team_heading}</h2>
              <p className="text-base text-slate-500 leading-relaxed mb-8">{hp.team_intro}</p>
              <Link to={hp.team_cta_link} data-testid="team-cta" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5">{hp.team_cta_label} <ArrowRight size={18} /></Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Meet the Founder ───────────────────────────── */}
      <FounderSection founder={hp.founder || {}} />

      {/* ── Section 6 · Client success stories ─────────── */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><SectionTitle eyebrow="Client success stories" title="Trusted, long-term relationships" subtitle="Real businesses, real outcomes. Here's what it's like to have CASS in your corner." center /></Reveal>
          {hp.client_logos?.length > 0 && (
            <Reveal>
              <div className="mb-14" data-testid="client-logos">
                <p className="text-center text-xs font-bold text-slate-400 uppercase tracking-widest mb-7">{hp.clients_strip_label}</p>
                <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-7">
                  {hp.client_logos.map((c, i) => (
                    <img loading="lazy" decoding="async" key={i} src={mediaUrl(c.logo, 400)} alt={c.name || "Client logo"} data-testid={`client-logo-${i}`} className="h-9 md:h-11 w-auto max-w-[150px] object-contain opacity-90 hover:opacity-100 hover:scale-105 transition-all duration-300" />
                  ))}
                </div>
              </div>
            </Reveal>
          )}
          {videos.length > 0 && (
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {videos.slice(0, 2).map((v, i) => (
                <Reveal key={v.id} delay={i * 100}>
                  <a href={`https://www.youtube.com/watch?v=${v.youtubeId}`} target="_blank" rel="noopener noreferrer" className="group block relative rounded-2xl overflow-hidden aspect-video shadow-lg" data-testid={`success-video-${i}`}>
                    <img loading="lazy" decoding="async" src={v.thumbnail} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-navy/50 group-hover:bg-navy/40 transition-colors flex items-center justify-center">
                      <span className="w-16 h-16 rounded-full bg-orange flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl"><Play size={26} className="text-white ml-1" fill="white" /></span>
                    </div>
                    <div className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-navy-deep/90 to-transparent"><p className="text-white font-semibold text-sm">{v.title}</p></div>
                  </a>
                </Reveal>
              ))}
            </div>
          )}
          {stories.length > 0 && (
            <div className="grid md:grid-cols-3 gap-6 mb-12" data-testid="home-success-stories">
              {stories.slice(0, 3).map((c, i) => (
                <Reveal key={c.id} delay={i * 80}>
                  <CaseStudyCard story={c} bg="bg-white" fallbackIcon={Star} testid={`home-story-${i}`} />
                </Reveal>
              ))}
            </div>
          )}
          <GoogleReviewsCarousel randomize />
          <div className="text-center mt-10">
            <Link to="/testimonials" className="inline-flex items-center gap-2 text-orange font-bold hover:gap-3 transition-all" data-testid="success-view-all">See all client stories <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* ── Section 7 · Technology marquee ─────────────── */}
      <section className="py-24 md:py-28 bg-navy overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><SectionTitle eyebrow="Technology" title={hp.tech_heading} subtitle={hp.tech_intro} center light /></Reveal>
        </div>
        <div className="marquee-mask relative mt-6" data-testid="tech-marquee" style={{ maskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)" }}>
          <div className="marquee-track gap-4">
            {[...hp.tech_partners, ...hp.tech_partners].map((p, i) => {
              const inner = p.logo ? (
                <img loading="lazy" decoding="async" src={mediaUrl(p.logo, 400)} alt={p.name || "Technology partner"} className="max-h-12 w-auto max-w-[150px] object-contain" data-testid={`tech-logo-${i}`} />
              ) : (
                <span className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full bg-orange" />
                  <span className="font-display font-extrabold text-2xl text-navy tracking-tight">{p.name}</span>
                </span>
              );
              const cls = "flex items-center justify-center bg-white border border-white/10 rounded-2xl px-8 mx-2 shrink-0 min-w-[190px] h-[96px] transition-transform hover:-translate-y-0.5";
              return p.url ? (
                <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" title={p.name ? `Visit ${p.name}` : "Visit partner website"} className={cls} data-testid={`tech-partner-link-${i}`}>{inner}</a>
              ) : (
                <div key={i} className={cls}>{inner}</div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Section 8 · Insights & resources ───────────── */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><SectionTitle eyebrow="Insights & resources" title={hp.insights_heading} subtitle={hp.insights_intro} center /></Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {latestArticle && (
              <Reveal delay={0}>
                <Link to={`/blog/${latestArticle.slug}`} className="group block h-full bg-white rounded-2xl border border-navy/5 shadow-sm overflow-hidden hover:-translate-y-1 transition-all" data-testid="insight-article">
                  <div className="aspect-[16/10] overflow-hidden bg-navy/5">{latestArticle.image && <img loading="lazy" decoding="async" src={latestArticle.image} alt={latestArticle.image_alt || latestArticle.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}</div>
                  <div className="p-6">
                    <span className="text-xs font-bold text-orange uppercase tracking-wider">Latest article</span>
                    <h3 className="font-display font-bold text-lg text-navy mt-2 mb-2 leading-snug">{latestArticle.title}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2">{latestArticle.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            )}
            {latestPodcast && (
              <Reveal delay={100}>
                <Link to="/podcast" className="group block h-full bg-white rounded-2xl border border-navy/5 shadow-sm overflow-hidden hover:-translate-y-1 transition-all" data-testid="insight-podcast">
                  <div className="aspect-[16/10] overflow-hidden bg-navy flex items-center justify-center">
                    {latestPodcast.image ? <img loading="lazy" decoding="async" src={latestPodcast.image} alt={latestPodcast.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <Mic size={40} className="text-orange" />}
                  </div>
                  <div className="p-6">
                    <span className="text-xs font-bold text-orange uppercase tracking-wider">Recent podcast</span>
                    <h3 className="font-display font-bold text-lg text-navy mt-2 mb-2 leading-snug">{latestPodcast.title}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2">{latestPodcast.description}</p>
                  </div>
                </Link>
              </Reveal>
            )}
            {featuredGuide && (
              <Reveal delay={200}>
                <div className="group h-full bg-white rounded-2xl border border-navy/5 shadow-sm overflow-hidden flex flex-col" data-testid="insight-guide">
                  <div className="aspect-[16/10] bg-navy flex items-center justify-center overflow-hidden">{featuredGuide.image ? <img loading="lazy" decoding="async" src={mediaUrl(featuredGuide.image, 800)} srcSet={mediaSrcSet(featuredGuide.image, [400, 800])} sizes="(max-width: 767px) 100vw, 400px" alt={featuredGuide.image_alt || featuredGuide.title} className="w-full h-full object-cover" /> : <Download size={40} className="text-orange" />}</div>
                  <div className="p-6 flex flex-col flex-1">
                    <span className="text-xs font-bold text-orange uppercase tracking-wider">Free guide</span>
                    <h3 className="font-display font-bold text-lg text-navy mt-2 mb-2 leading-snug">{featuredGuide.title}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2 mb-4 flex-1">{featuredGuide.description}</p>
                    {featuredGuide.file_url
                      ? <button onClick={() => setActiveGuide(featuredGuide)} data-testid="insight-guide-download" className="inline-flex items-center gap-2 self-start bg-navy hover:bg-navy-deep text-white font-bold text-sm px-6 py-3 rounded-full transition-all">Free Download <Download size={15} /></button>
                      : <span className="inline-flex items-center gap-2 self-start text-slate-400 font-semibold text-sm">Coming soon</span>}
                  </div>
                </div>
              </Reveal>
            )}
          </div>
          <div className="text-center mt-12 flex flex-wrap items-center justify-center gap-3">
            <Link to="/more-than-numbers/business-assessment-tools" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5" data-testid="insights-cta">{hp.insights_cta_label} <ArrowRight size={18} /></Link>
            <Link to="/business-health-check" className="inline-flex items-center gap-2 bg-white border-2 border-navy/15 hover:border-orange text-navy font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5" data-testid="home-health-check-cta">Take the free Business Growth Health Check <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>

      <GuideDownloadModal guide={activeGuide} open={!!activeGuide} onClose={() => setActiveGuide(null)} />

      {/* ── Section 8b · Numbers Calculator promo ──────── */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="relative overflow-hidden rounded-3xl bg-navy text-white p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-8" data-testid="home-calculator-promo">
            <div className="flex-1">
              <span className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4"><Calculator size={14} className="text-orange" /> Free interactive tool</span>
              <h2 className="font-display font-extrabold text-2xl md:text-4xl leading-tight max-w-xl">Know your numbers. Understand what they're telling you.</h2>
              <p className="text-slate-300 mt-4 max-w-xl leading-relaxed">Enter a few figures and instantly calculate your key business numbers — gross margin, break-even, cash runway, debtor days and more — each explained in plain English, with a personalised action plan.</p>
              <Link to="/business-numbers-calculator" data-testid="home-calculator-cta" className="mt-6 inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5">Try the Key Business Numbers Calculator <ArrowRight size={18} /></Link>
            </div>
            <div className="shrink-0 grid grid-cols-2 gap-3 w-full md:w-72">
              {[["37.4%", "Gross margin"], ["£46,875", "Break-even / mo"], ["54 days", "Debtor days"], ["3.8 mo", "Cash runway"]].map(([v, l]) => (
                <div key={l} className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
                  <p className="font-display font-extrabold text-xl md:text-2xl text-white">{v}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── How getting started works ──────────────────── */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="home-how-it-works">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal><SectionTitle eyebrow="How it works" title="Getting started is simple" subtitle="No pressure, no jargon and no long tie-ins — just a clear, friendly path to better numbers." center /></Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              ["01", "A free discovery call", "We start with a relaxed, no-obligation chat to understand your business, your goals and where you'd like more support."],
              ["02", "A tailored proposal", "Because every business is different, we build a recommendation around exactly what you need — with clear, fixed monthly pricing."],
              ["03", "A smooth onboarding", "If it's a good fit, we handle the switch for you — liaising with your previous accountant and getting you set up with zero hassle."],
            ].map(([num, title, text], i) => (
              <Reveal key={num} delay={i * 100}>
                <div className="h-full bg-white rounded-3xl border border-slate-100 p-8 hover:-translate-y-1 hover:shadow-lg transition-all">
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-orange/10 text-orange font-display font-extrabold text-lg mb-5">{num}</span>
                  <h3 className="font-display font-bold text-navy text-lg mb-2">{title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/switching-accountants" data-testid="how-it-works-switching" className="inline-flex items-center gap-2 text-navy font-bold hover:text-orange transition-colors">Thinking of switching accountants? See how easy it is <ArrowRight size={17} /></Link>
          </div>
        </div>
      </section>

      {/* ── Section 9 · Final CTA ──────────────────────── */}
      <section className="numbers-pattern text-white relative">
        <FloatingNumbers />
        <div className="relative max-w-3xl mx-auto px-6 py-24 md:py-28 text-center">
          <Reveal>
            <h2 className="font-display font-extrabold text-3xl md:text-5xl leading-tight">{hp.final_heading}</h2>
            <p className="mt-6 text-slate-300 leading-relaxed max-w-2xl mx-auto">{hp.final_intro}</p>
            <div className="mt-9 flex flex-wrap gap-4 justify-center">
              <Link to={hp.final_primary_link} data-testid="final-primary-cta" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:shadow-xl hover:shadow-orange/30 hover:-translate-y-0.5">{hp.final_primary_label} <ArrowRight size={18} /></Link>
              <Link to={hp.final_secondary_link} data-testid="final-secondary-cta" className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-bold px-8 py-4 rounded-full transition-all">{hp.final_secondary_label}</Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mobile sticky CTA (does not clash with bottom-right chat bubble) */}
      <Link
        to="/contact"
        data-testid="mobile-sticky-cta"
        className="lg:hidden fixed bottom-4 left-4 z-40 inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold text-sm px-5 py-3 rounded-full shadow-xl shadow-orange/30 transition-all"
      >
        Let's Talk <ArrowRight size={16} />
      </Link>
    </div>
  );
}
